import React, {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import AccountCard from './account/AccountCard';
import ProfileForm, {ProfileFormValues} from './account/ProfileForm';
import EmailCard from './account/EmailCard';
import ChangePasswordCard, {ChangePasswordValues} from './account/ChangePasswordCard';
import VideoEditorCard from './account/VideoEditorCard';
import SubscriptionManagementCard from './subscription/SubscriptionManagementCard';
import PricingContainer from './pricing.container';
import {useVideoEditorPreference} from './useVideoEditorPreference';
import {
  changeEmail,
  changePassword,
  fetchUserProfile,
  syncProfileEmail,
  updateUserProfile,
} from '../store/slices/authThunks';
import {fetchSubscriptions} from '../store/slices/subscriptionThunks';
import {
  authEmailSelector,
  changeEmailApiSelector,
  changePasswordApiSelector,
  pendingEmailSelector,
  updateUserProfileApiSelector,
  userDisplayNameSelector,
  userEmailSelector,
} from '../store/selectors/auth.selectors';
import {
  activeSubscriptionSelector,
  subscriptionsApiSelector,
} from '../store/selectors/subscription.selectors';
import {isProSelector, planLabelSelector} from '../store/selectors/entitlement.selectors';
import {useToast} from './Toast';

/**
 * The signed-in account page: profile details, credentials, billing.
 *
 * This replaces the marketing site's split /profile + /dashboard. The dashboard was only a plan
 * picker and a desktop-download CTA, and the app's own landing screen is /welcome.
 *
 * Name, email and password are three separate cards because they are three different writes:
 * name goes to the CensorMe profile row, while email and password go to Supabase auth and only
 * email needs a confirmation round trip. The old single "Save Changes" form implied they all
 * landed together, and its email field wrote the profile row without ever telling auth.
 */
const AccountContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();

  const name = useAppSelector(userDisplayNameSelector);
  const profileEmail = useAppSelector(userEmailSelector);
  const authEmail = useAppSelector(authEmailSelector);
  const pendingEmail = useAppSelector(pendingEmailSelector);
  const updateApi = useAppSelector(updateUserProfileApiSelector);
  const emailApi = useAppSelector(changeEmailApiSelector);
  const passwordApi = useAppSelector(changePasswordApiSelector);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const subscriptionsApi = useAppSelector(subscriptionsApiSelector);
  const isPro = useAppSelector(isProSelector);
  const planLabel = useAppSelector(planLabelSelector);

  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const videoEditorPreference = useVideoEditorPreference();

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (subscriptionsApi.status === 'idle') {
      dispatch(fetchSubscriptions());
    }
  }, [dispatch, subscriptionsApi.status]);

  // Auth is the source of truth for the address. This closes the loop after a confirmed change —
  // the confirmation link updates auth and nothing else — and heals rows the old form desynced.
  useEffect(() => {
    if (authEmail && profileEmail && authEmail.toLowerCase() !== profileEmail.toLowerCase()) {
      dispatch(syncProfileEmail());
    }
  }, [dispatch, authEmail, profileEmail]);

  const handleProfileSubmit = async (values: ProfileFormValues) => {
    try {
      await dispatch(updateUserProfile({name: values.name})).unwrap();
      showToast('Name updated', 'success');
    } catch (error: any) {
      showToast(`Failed to update name: ${error}`, 'error');
    }
  };

  const handleEmailSubmit = async (email: string) => {
    setEmailSentTo(null);
    try {
      await dispatch(changeEmail(email)).unwrap();
      setEmailSentTo(email);
      showToast('Confirmation email sent', 'success');
    } catch {
      // The rejection message is in the slice and renders on the card.
    }
  };

  const handlePasswordSubmit = async (values: ChangePasswordValues) => {
    setPasswordChanged(false);
    try {
      await dispatch(changePassword({
        email: authEmail,
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })).unwrap();
      setPasswordChanged(true);
      showToast('Password updated', 'success');
    } catch {
      // The rejection message is in the slice and renders on the card.
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="flex items-center gap-2.5 text-xl font-semibold text-gray-100">
                        Account
            {isPro && (
              <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-medium text-white">
                {planLabel}
              </span>
            )}
          </h1>
          <p className="mt-0.5 text-xs text-gray-500">Manage your profile, sign-in details and subscription.</p>
        </div>

        <div className="space-y-4">
          <AccountCard title="Profile" description="How your name appears in the app.">
            <ProfileForm
              initialValues={{name}}
              saving={updateApi.status === 'pending'}
              onSubmit={handleProfileSubmit}
            />
          </AccountCard>

          <EmailCard
            currentEmail={authEmail}
            pendingEmail={pendingEmail}
            error={emailApi.error}
            submitting={emailApi.status === 'pending'}
            sentTo={emailSentTo}
            onSubmit={handleEmailSubmit}
          />

          <ChangePasswordCard
            error={passwordApi.error}
            submitting={passwordApi.status === 'pending'}
            changed={passwordChanged}
            onSubmit={handlePasswordSubmit}
          />

          <VideoEditorCard
            videoEditor={videoEditorPreference.videoEditor}
            onSelect={videoEditorPreference.choose}
            onClear={videoEditorPreference.clear}
            isSaving={videoEditorPreference.isSaving}
          />

          {activeSubscription && (
            <SubscriptionManagementCard subscription={activeSubscription} />
          )}
        </div>
      </div>

      {/* Plan cards live below the account form — the same container the landing page renders. */}
      <PricingContainer />
    </div>
  );
};

export default AccountContainer;
